import { OwnOfferAsset, AssetWithBalance } from 'core/logic/asset/asset.types'
import { i18n } from 'translate/i18n'
import CollectionItemView from './CollectionItem.view'
import CollectionItemShimmer from './Shimmer/CollectionItemShimmer.view'
import {
  PendingPurchase,
  PurchaseType,
} from 'core/logic/purchase/purchase.types'
import { DeliveryType } from 'core/logic/delivery/delivery.types'
import { ComponentProps, useMemo } from 'react'
import { DropTypeBase } from 'core/logic/drop/drop.types'
import { CollectionItemType } from './CollectionItem.types'
import { useAsset } from 'context/Asset'

export interface DropItemLogicProps {
  assetOrDeliveryOrPurchase?:
    | AssetWithBalance
    | OwnOfferAsset
    | PendingPurchase
    | DeliveryType
  last?: boolean
  disabled?: boolean
  showStickyTop?: boolean
  orientation?: string
  onClick?: (id: string) => void
  'data-testid'?: string
}

const CollectionItemLogic: React.FC<DropItemLogicProps> = ({
  assetOrDeliveryOrPurchase,
  last = false,
  orientation,
  onClick,
  ...props
}) => {
  const { asset, type } = useMemo(() => {
    if (!assetOrDeliveryOrPurchase) return {}
    if ('_type' in assetOrDeliveryOrPurchase) {
      switch (assetOrDeliveryOrPurchase._type) {
        case 'delivery':
          return {
            asset:
              assetOrDeliveryOrPurchase.offer?.asset ||
              assetOrDeliveryOrPurchase.asset!,
            type: CollectionItemType.DELIVERY,
          }
      }
    }
    if (
      'type' in assetOrDeliveryOrPurchase &&
      assetOrDeliveryOrPurchase.type === 'airdrop'
    ) {
      return {
        asset: (assetOrDeliveryOrPurchase as AssetWithBalance).asset!,
        type: CollectionItemType.DELIVERY,
      }
    }
    if ('nextAction' in assetOrDeliveryOrPurchase) {
      return {
        asset: undefined,
        type: CollectionItemType.PURCHASE,
      }
    }
    if (
      'offer' in assetOrDeliveryOrPurchase &&
      !!assetOrDeliveryOrPurchase.offer
    ) {
      const offer = assetOrDeliveryOrPurchase.offer as DropTypeBase
      return {
        asset: offer.asset,
        type:
          'paymentStatus' in assetOrDeliveryOrPurchase
            ? CollectionItemType.PURCHASE
            : CollectionItemType.DELIVERY,
      }
    } else {
      return {
        asset: assetOrDeliveryOrPurchase,
        type: CollectionItemType.ASSET,
      }
    }
  }, [assetOrDeliveryOrPurchase])

  const purchaseOrDeliveryInfo = useMemo(() => {
    return {
      ...(type === CollectionItemType.DELIVERY
        ? {
            type,
            delivery: assetOrDeliveryOrPurchase as DeliveryType,
          }
        : type === CollectionItemType.PURCHASE
        ? {
            type,
            purchase: assetOrDeliveryOrPurchase as PendingPurchase,
          }
        : {
            type: type!,
          }),
    }
  }, [type, assetOrDeliveryOrPurchase])

  if (
    asset === undefined &&
    [CollectionItemType.PURCHASE, CollectionItemType.DELIVERY].includes(type!)
  ) {
    return (
      <DigitalTwinCollectionItem
        {...purchaseOrDeliveryInfo}
        type={type!}
        purchaseOrDelivery={assetOrDeliveryOrPurchase as PurchaseType}
        last={last}
        orientation={orientation}
        onClick={onClick}
        data-testid={props['data-testid']}
      />
    )
  }

  if (!asset) {
    return <CollectionItemShimmer last={last} />
  }

  const { name, description } = asset[i18n.language] ?? asset

  const {
    media: { coverUrl, animationUrl, imageUrl },
    balance,
    id,
  } = asset

  return (
    <CollectionItemView
      itemName={name}
      itemImage={coverUrl || imageUrl}
      itemAnimation={animationUrl}
      itemDescription={description}
      itemCount={balance}
      last={last}
      id={id}
      orientation={orientation}
      onClick={onClick}
      offerDetails={asset.offerResume}
      asset={asset}
      data-testid={props['data-testid']}
      {...purchaseOrDeliveryInfo}
    />
  )
}

function DigitalTwinCollectionItem({
  type,
  ...props
}: Omit<
  ComponentProps<typeof CollectionItemView>,
  | 'asset'
  | 'asset'
  | 'itemName'
  | 'itemImage'
  | 'itemAnimation'
  | 'itemDescription'
  | 'itemCount'
  | 'id'
> & {
  purchaseOrDelivery: PurchaseType | DeliveryType
}) {
  const query = useMemo<Parameters<typeof useAsset>>(() => {
    if (props.purchaseOrDelivery.sellOrder?.asset.id)
      return ['id', [props.purchaseOrDelivery.sellOrder?.asset.id!]]
    const purchase = props.purchaseOrDelivery as PurchaseType
    if (purchase.nextAction?.data.orderRequest.asset.cid)
      return ['cid', [purchase.nextAction?.data.orderRequest.asset.cid]]
    return [
      'token',
      [Number(purchase.nextAction?.data.orderRequest.asset.tokenId)],
    ]
  }, [])
  const relatedAsset = useAsset(...query)

  if (!relatedAsset) {
    return <CollectionItemShimmer last={props.last} />
  }

  const { name, description } = relatedAsset[i18n.language] ?? relatedAsset

  const {
    media: { coverUrl, animationUrl, imageUrl },
    balance,
    id,
  } = relatedAsset

  const propsToPassOn = {
    ...props,
    asset: relatedAsset,
    itemName: name,
    itemImage: coverUrl || imageUrl,
    itemAnimation: animationUrl,
    itemDescription: description,
    itemCount: balance,
    id: id,
  }

  if (type === CollectionItemType.DELIVERY)
    return (
      <CollectionItemView
        {...propsToPassOn}
        type={CollectionItemType.DELIVERY}
        delivery={props.purchaseOrDelivery as DeliveryType}
      />
    )
  if (type === CollectionItemType.PURCHASE)
    return (
      <CollectionItemView
        {...propsToPassOn}
        type={CollectionItemType.PURCHASE}
        purchase={props.purchaseOrDelivery as PendingPurchase}
      />
    )
}

export default CollectionItemLogic
